import { useState } from 'react';
import { goToDynamicPath } from '../redirect';
import { getOnboardingRedirectPath } from '../../services/route';
import { isObjectEmpty } from '../../services/object';
import { logComponentEvent, EVENTCAT_RESP_DASHBOARD } from '../logging/componentEvent';

export function useHeaderAndMenuNavigation({
  logout,
  browserHistory,
  hasResp,
  pendingApplications,
  hasPlanAccount,
  environmentSettings,
  linkOverrides = {}
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { isGiftingEnabled } = environmentSettings || {};

  const toggleMenu = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const isOpen = Boolean(anchorEl);
  const links = {
    home: {
      id: `home-link`,
      href: () => (hasResp ? goToDynamicPath('/resp/main', browserHistory) : goToDynamicPath('/main', browserHistory)),
      localizedLabelKey: 'headerNavLinks.home',
      isVisible: true
    },
    money: {
      id: `money-link`,
      href: () => goToDynamicPath('/main/money', browserHistory),
      localizedLabelKey: 'headerNavLinks.money',
      isVisible: true
    },
    offers: {
      id: `offers-link`,
      href: () => goToDynamicPath('/main/rewards', browserHistory),
      localizedLabelKey: 'headerNavLinks.offers',
      isVisible: true
    },
    tools: {
      id: `tools-link`,
      href: () => goToDynamicPath('/main/tools', browserHistory),
      localizedLabelKey: 'headerNavLinks.tools',
      isVisible: true
    },
    addAChild: {
      id: `add-a-child-menu-item`,
      href: () => goToDynamicPath('/resp/main/kyc-review?nextid=20', browserHistory),
      localizedLabelKey: 'headerNavMenuItems.addAChild',
      isVisible: true
    },
    linkABankAccount: {
      id: `link-a-bank-account-menu-item`,
      href: () => goToDynamicPath('/resp/main/bank-accounts', browserHistory),
      localizedLabelKey: 'headerNavMenuItems.linkABankAccount',
      isVisible: true
    },
    addAPlan: {
      id: `add-a-plan-menu-item`,
      href: () => goToDynamicPath('/resp/main/kyc-review?nextid=10', browserHistory),
      localizedLabelKey: hasPlanAccount ? 'headerNavMenuItems.addAPlan' : 'headerNavMenuItems.openResp',
      isVisible: true
    },
    giftingLink: {
      id: `gifting-link-menu-item`,
      href: () => {
        logComponentEvent('NavigationMenu', 'GiftingLinkClick', {
          eventCategory: EVENTCAT_RESP_DASHBOARD
        });
        return goToDynamicPath('/resp/main/send-a-gift', browserHistory);
      },
      localizedLabelKey: 'headerNavMenuItems.giftingLink',
      isVisible: true
    },
    transferIn: {
      id: `transfer-in-menu-item`,
      href: () => goToDynamicPath('/resp/main/transfer-in', browserHistory),
      localizedLabelKey: 'headerNavMenuItems.transferIn',
      isVisible: true
    },
    addCosubscriber: {
      id: `add-cosubscriber-menu-item`,
      href: () => goToDynamicPath('/resp/main/add-a-cosubscriber', browserHistory),
      localizedLabelKey: 'headerNavMenuItems.addCosubscriber',
      isVisible: true
    },
    settings: {
      id: `settings-menu-item`,
      href: () => goToDynamicPath('/main/settings', browserHistory),
      localizedLabelKey: 'headerNavMenuItems.settings',
      isVisible: true
    },
    documents: {
      id: `documents-menu-item`,
      href: () => goToDynamicPath('/resp/main/documents', browserHistory),
      localizedLabelKey: 'headerNavMenuItems.documents',
      isVisible: true
    },
    // if user goes directly to onboarding or newbaby-onboarding from signup, there is no pendingApplications loaded yet
    openResp: {
      id: `open-resp-menu-item`,
      href: () =>
        pendingApplications?.length === 0
          ? goToDynamicPath('/resp/main/newonboarding', browserHistory)
          : goToDynamicPath('/resp' + getOnboardingRedirectPath(pendingApplications), browserHistory),
      localizedLabelKey: 'headerNavMenuItems.openResp',
      isVisible: true
    },
    logout: {
      id: `logout-menu-item`,
      href: () => logout(),
      localizedLabelKey: 'headerNavMenuItems.logout',
      isVisible: true
    }
  };

  const getlinksWithOverrides = (links, linkOverrides = {}) =>
    Object.keys(links).reduce((acc, key) => {
      acc[key] = {
        ...links[key],
        ...(linkOverrides[key] || {})
      };
      return acc;
    }, {});

  const shouldRenderOverrides = !isObjectEmpty(linkOverrides);

  const {
    home,
    money,
    offers,
    addAChild,
    addAPlan,
    giftingLink,
    linkABankAccount,
    transferIn,
    addCosubscriber,
    settings,
    tools,
    documents,
    openResp,
    logout: logoutLink
  } = shouldRenderOverrides ? getlinksWithOverrides(links, linkOverrides) : links;

  const headerLinks = [home, money, offers, tools].filter(link => link.isVisible);
  const menuLinks = [
    ...(hasResp
      ? hasPlanAccount
        ? [
            addAChild,
            linkABankAccount,
            addAPlan,
            ...(isGiftingEnabled ? [giftingLink] : []),
            transferIn,
            addCosubscriber,
            documents
          ]
        : [addAPlan, documents]
      : [openResp]),
    settings,
    logoutLink
  ].filter((link) => link.isVisible);

  return {
    headerLinks,
    isOpen,
    anchorEl,
    toggleMenu,
    menuLinks
  };
}
